import React, { ReactElement, useEffect, useState } from "react";
import {
    FormFieldReplacementInput,
    FormFieldType, FormType,
    WorkflowStepParameterInput, useGetActionFieldsQuery,
} from "../../../__generated__/api";
import FieldTypeValueDisplay from "../../form-field/FieldTypeValueDisplay";
import { LoadingIcon } from "../../loading/Loading";
import RequestTypes from "../../pickers/request-types/RequestTypes";
import { ActionProps } from "./base";
import BooleanSelect from "../../pickers/boolean/BooleanSelect";
import Users from "../../pickers/users/Users";
import ExpandingSection from "../../expanding-section/ExpandingSection";
import EditableControl, { ControlType } from "../../editable-text/EditableText";
import { isDeepEqual } from "../../../api";

const REQUEST_TYPE = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,requestType)";
const REQUESTER_TYPE = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,requester)";
const PARAMETERS_FIELD_ID = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,parameters)";
const SHOULD_EXECUTE_FIELD_ID = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,shouldExecute)";
const MESSAGE_FIELD_ID = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,message)";

export default function CreateTicketSettings(props: ActionProps): ReactElement {
    const requestType = props.values.find(row => row.value.fieldId === REQUEST_TYPE);
    const shouldExecuteParam = props.values.find(row => row.value.fieldId === SHOULD_EXECUTE_FIELD_ID)?.value?.boolean;
    const userParam = props.values.find(row => row.value.fieldId === REQUESTER_TYPE)?.value?.string;
    const messageParam = props.values.find(row => row.value.fieldId === MESSAGE_FIELD_ID)?.value?.string;

    const valuesType = props.values.find(row => row.value.fieldId === PARAMETERS_FIELD_ID);

    const [shouldExecute, setShouldExecute] = useState(shouldExecuteParam ?? true);
    const [userId, setUserId] = useState<string | undefined>(userParam as string);
    const [message, setMessage] = useState<string | undefined>(messageParam as string);

    const [parameters, setParameters] = useState<WorkflowStepParameterInput[]>(JSON.parse(valuesType?.value?.string ?? "[]"));
    const [replacements, setReplacements] = useState<FormFieldReplacementInput[]>(props.values[0]?.replacement ?? []);

    const [selectedActionType, setSelectedActionType] = useState<{id: string, name: string} | undefined>(requestType ? {
        id: requestType?.value.string as string,
        name: requestType?.value.displayValue as string,
    } : undefined);

    const { data, loading } = useGetActionFieldsQuery({
        skip: !shouldExecute || selectedActionType == null,
        variables: {
            id: selectedActionType?.id,
        },
    });

    const createValues = () => [
        {
            fieldId: REQUEST_TYPE,
            type: FormFieldType.AsyncDropdown,
            displayValue: selectedActionType?.name,
            string: selectedActionType?.id,
        },
        {
            fieldId: SHOULD_EXECUTE_FIELD_ID,
            type: FormFieldType.AsyncDropdown,
            displayValue: shouldExecute?.toString(),
            boolean: shouldExecute,
        },
        {
            fieldId: PARAMETERS_FIELD_ID,
            type: FormFieldType.LongText,
            displayValue: parameters.firstElement?.value?.string ?? "",
            string: JSON.stringify(parameters),
        },
        {
            fieldId: REQUESTER_TYPE,
            type: FormFieldType.User,
            displayValue: userId ?? "",
            string: userId,
        },
        {
            fieldId: MESSAGE_FIELD_ID,
            type: FormFieldType.LongText,
            displayValue: message ?? "",
            string: message,
        },
    ];

    useEffect(() => {
        const values = createValues();
        if (!isDeepEqual(values, props.values.toIdList("value"))) {
            setShouldExecute(shouldExecuteParam ?? true);
            setUserId(userParam as string);
            setMessage(messageParam as string);

            setParameters(JSON.parse(valuesType?.value?.string ?? "[]"));
            setReplacements(props.values[0]?.replacement ?? []);
        }
    }, [props.values]);

    useEffect(() => {
        props.onChange({
            values: createValues(),
            replacements,
        });
    }, [selectedActionType?.id, parameters, message, userId, shouldExecute]);

    const fieldMap = data?.formFields.toIdMap("id") ?? {};
    return (
        <div className="">
            <div className="mb-4">
                <div className="">
                    Request Type
                </div>
                <div className="">
                    <RequestTypes
                        type={FormType.Default}
                        onSelected={(value, text) => {
                            setSelectedActionType({ id: value, name: text });
                            setParameters([]);
                        }}
                        value={selectedActionType?.id}
                    />
                </div>
                <small>
                    Which reequest type should this new ticket be?
                </small>
            </div>

            <div className="mb-4">
                <div className="">
                    Requester
                </div>
                <div className="">
                    <Users
                        value={userId}
                        input={props.input}
                        replacements={replacements}
                        onSelected={(value, _text, replace) => {
                            setUserId(value);
                            setReplacements([
                                ...replacements,
                                ...(replace ?? []),
                            ]);
                        }}
                    />
                </div>
                <small>
                    Who should be set as the requester of the ticket?
                </small>
            </div>

            {shouldExecute && (
                <>
                    {loading && <LoadingIcon />}

                    {data?.form?.fields?.map(descriptor => {
                        const field = fieldMap[descriptor.fieldId];
                        if (!field) {
                            return null;
                        }
                        const value = parameters.find(row => row.value.fieldId === field.id);
                        return (

                            <FieldTypeValueDisplay
                                key={field.id}
                                shouldShowFieldName
                                className="mb-3"
                                inputClassName="form-control"
                                field={field}
                                replaceableInput={props.input}
                                replacements={value?.replacement as FormFieldReplacementInput[]}
                                value={value?.value}
                                onChange={(changedValue, replace) => {
                                    const copy = Array.from(parameters);
                                    const index = parameters.findIndex(row => row.value.fieldId === field.id);
                                    if (index >= 0) {
                                        copy[index] = {
                                            value: changedValue,
                                            replacement: replace,
                                        };
                                    } else {
                                        copy.push({
                                            value: changedValue,
                                            replacement: replace,
                                        });
                                    }
                                    setParameters(copy);
                                }}
                            />

                        );
                    })}

                </>
            )}

            <ExpandingSection title="Advanced">

                <div className="mb-4">
                    <div className="">
                        Should Execute Automation
                    </div>
                    <div className="">
                        <BooleanSelect
                            value={shouldExecute}
                            onChange={value => setShouldExecute(value ?? true)}
                        />
                    </div>
                    <small>
                        Should execute any automation associated with the request type.
                    </small>
                </div>
                <div className="mb-4">
                    <div className="">
                        Message
                    </div>
                    <div className="">
                        <EditableControl
                            type={ControlType.LONG_TEXT}
                            value={message}
                            isEditing
                            inputClassName="form-control"
                            onChange={(text, replace) => {
                                setMessage(text as string);
                                setReplacements([
                                    ...replacements,
                                    ...(replace ?? []),
                                ]);
                            }}
                            replaceableInput={props.input}
                            replacements={replacements}
                        />
                    </div>
                    <small>
                        What should the message that is sent to the user say?
                    </small>
                </div>

            </ExpandingSection>

        </div>

    );
}
